import { memo } from 'react';

import { Button } from '@air/primitive-button';
import { useBreakpointsContext } from '@air/provider-media-query';
import { useAirModal } from '@air/provider-modal';
import { useShortIdContext } from '@air/provider-short-id';

import { SharePublicBoardModal } from '~/components/Modals/SharePublicBoardModal';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { PublicBoardDownloadMenu } from '~/components/Shared/HeaderBar/PublicBoardDownloadMenu';
import { SHARE_BUTTON } from '~/constants/testIDs';
import { useAppliedFiltersCount } from '~/hooks/filters/useAppliedFiltersCount';
import { useSearchParams } from '~/hooks/search/useSearchParams';
import { type BoardPageBoard } from '~/store/centralizedBoard/types';
import { getPublicSubboardShareUrl } from '~/swr-hooks/shortUrl/utils';
import { canGenerateZip } from '~/utils/permissions/taskPermissions';

export type PublicBoardHeaderActionsProps = {
  board: BoardPageBoard;
};

export const PublicBoardHeaderActions = memo(({ board }: PublicBoardHeaderActionsProps) => {
  const { isAboveMediumScreen } = useBreakpointsContext();
  const [showSharePublicBoardModal] = useAirModal(SharePublicBoardModal);
  const { permissions } = usePublicPermissions();
  const { presentationSettings, shortId } = useShortIdContext();
  const { search } = useSearchParams();
  const { filtersCount } = useAppliedFiltersCount();
  const canDownload = canGenerateZip(permissions);
  const hideDownloadAll = presentationSettings?.hideDownloadAll === true;
  const canShowDownloadMenu = canDownload && !hideDownloadAll;

  if (!isAboveMediumScreen || !!filtersCount || !!search) {
    return null;
  }

  const onShareClick = () =>
    showSharePublicBoardModal({
      id: board.id,
      url: getPublicSubboardShareUrl(shortId, board.id),
      trackLocation: 'header',
    });

  return (
    <div className="flex shrink-0">
      {canDownload && (
        <Button size="large" appearance="filled" color="grey" data-testid={SHARE_BUTTON} onClick={onShareClick}>
          Copy Link
        </Button>
      )}
      {canShowDownloadMenu && <PublicBoardDownloadMenu board={board} />}
    </div>
  );
});

PublicBoardHeaderActions.displayName = 'PublicBoardHeaderActions';
